import { createContext, createElement, useCallback, useContext, useEffect, useState, type ReactNode } from "react";

import { useApi } from "../api/ApiProvider";
import { ApiError } from "../api/client";
import type { BetriebStatusResponse } from "../api/types";

export interface ShellStatus {
  /** Last successful GET /betrieb/status; null until loaded or when it failed. */
  status: BetriebStatusResponse | null;
  /** Set when the last attempt failed (403, 501, network); the strip stays silent then. */
  error: string | null;
  /** Re-read now (Betrieb "neu prüfen"); the 60-s rhythm continues unchanged. */
  reload: () => void;
}

const SHELL_REFRESH_MS = 60_000;

const ShellStatusContext = createContext<ShellStatus>({ status: null, error: null, reload: () => {} });

function errorText(err: unknown): string {
  if (err instanceof ApiError && err.status === 403) {
    return "Für den Betriebsstatus fehlt Ihnen die Rolle. Die IT vergibt sie in Keycloak.";
  }
  return "Der Betriebsstatus konnte nicht geladen werden.";
}

/**
 * One request for the whole shell: GET /api/v1/betrieb/status on mount and
 * every 60 s. Feeds the system strip, the Rückrufe counter, Heute and
 * Betrieb. Replaces the former five-source shell poll; nothing else in the
 * shell asks the API in a rhythm.
 */
export function ShellStatusProvider({ children }: { children: ReactNode }) {
  const api = useApi();
  const [state, setState] = useState<{ status: BetriebStatusResponse | null; error: string | null }>({
    status: null,
    error: null,
  });
  const [nonce, setNonce] = useState(0);
  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      api.getBetriebStatus().then(
        (status) => {
          if (cancelled) return;
          // Eine Antwort ohne Komponentenkarte erfüllt den Vertrag nicht: wie ein Fehler behandeln.
          if (typeof status?.komponenten !== "object" || status.komponenten === null) {
            setState({ status: null, error: errorText(null) });
          } else {
            setState({ status, error: null });
          }
        },
        // Kein Vorgabewert "live": ohne Antwort bleibt der Streifen stumm.
        (err: unknown) => !cancelled && setState({ status: null, error: errorText(err) }),
      );
    };
    load();
    const timer = setInterval(() => {
      if (!document.hidden) load();
    }, SHELL_REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [api, nonce]);

  return createElement(ShellStatusContext.Provider, { value: { ...state, reload } }, children);
}

export function useShellStatus(): ShellStatus {
  return useContext(ShellStatusContext);
}
